// The High priority flag: a small flag button on each board row and in the sidebar header. Filled
// amber when the account is flagged, a faint outline when not. For someone who cannot change it,
// it is locked (aria-disabled, so the hover still says why) rather than hidden, and a flagged row
// still shows its flag to everyone. Rules in `priorityFlag.ts`.

import type { MouseEvent } from 'react';
import type { BookAccount } from '../api/types';
import type { BookColumn, CellContext, PriorityCellContext } from './columns';
import { isHighPriority, priorityBlockedReason, priorityTitle } from './priorityFlag';

function FlagIcon({ filled }: { filled: boolean }) {
  return (
    <svg className="prioicon" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true">
      <path d="M2.5 11V1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" fill="none" />
      <path d="M3 1.8h6.6l-1.6 2.6 1.6 2.6H3z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"
        fill={filled ? 'currentColor' : 'none'} />
    </svg>
  );
}

export function PriorityFlagButton({
  row, priority, withLabel = false,
}: { row: BookAccount; priority: PriorityCellContext; withLabel?: boolean }) {
  const flagged = isHighPriority(row);
  const blockedReason = priorityBlockedReason(priority.viewer, row);
  const pending = priority.pendingId === row.id;
  const locked = Boolean(blockedReason) || pending;
  const title = priorityTitle(row, blockedReason);

  // A click never opens the row's panel, and a locked flag does nothing.
  function onClick(event: MouseEvent) {
    event.stopPropagation();
    if (locked) return;
    priority.onToggle(row);
  }

  const className = 'prioflag' + (flagged ? ' on' : '') + (blockedReason ? ' locked' : '') + (pending ? ' busy' : '')
    + (withLabel ? ' labelled' : '');
  return (
    <button
      type="button"
      className={className}
      title={title}
      aria-label={withLabel ? undefined : `High priority: ${row.account}`}
      aria-pressed={flagged}
      aria-disabled={locked}
      onClick={onClick}
    >
      <FlagIcon filled={flagged} />
      {withLabel ? <span>{flagged ? 'High priority' : 'Mark high priority'}</span> : null}
    </button>
  );
}

// The read-only flag for a table drawn without a toggle context: a flagged row still shows it.
function StaticFlag({ row }: { row: BookAccount }) {
  if (!isHighPriority(row)) return null;
  return (
    <span className="prioflag on locked" title={priorityTitle(row, null).split(' Click')[0]}>
      <FlagIcon filled />
    </span>
  );
}

function PriorityCell({ row, context }: { row: BookAccount; context: CellContext }) {
  if (!context.priority) return <StaticFlag row={row} />;
  return <PriorityFlagButton row={row} priority={context.priority} />;
}

// The flag column: 26px, just left of Account. Sorts flagged rows first on a click.
export const PRIORITY_COLUMN: BookColumn = {
  key: 'high_priority',
  label: '',
  width: '26px',
  hint: 'High priority. Admins and leadership can mark any account; a strategist their own.',
  sortValue: row => (isHighPriority(row) ? 0 : 1),
  accessor: row => (isHighPriority(row) ? 'High priority' : ''),
  cell: (row, context) => <PriorityCell row={row} context={context} />,
};

// The columns with the flag column placed just before Account (or first, with no Account column).
export function withPriorityColumn(columns: BookColumn[]): BookColumn[] {
  if (columns.some(column => column.key === PRIORITY_COLUMN.key)) return columns;
  const accountIndex = columns.findIndex(column => column.key === 'account');
  if (accountIndex < 0) return [PRIORITY_COLUMN, ...columns];
  return [...columns.slice(0, accountIndex), PRIORITY_COLUMN, ...columns.slice(accountIndex)];
}
